import { useRef } from "react";
import { isPinchZoomed } from "./usePinchZoomed";

const swipeThresholdInPx = 50;

export interface SwipeCallbacks {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  /** without vertical callbacks, vertical gestures are left alone so the content keeps scrolling */
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

export interface SwipeHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchEnd: (event: React.TouchEvent) => void;
}

/**
 * The dominant direction of a gesture decides which callback is called, if any.
 * Multi touch gestures (pinch-zoom) are ignored, and so are all gestures while the page is
 * pinch-zoomed, because a one finger gesture then pans the zoomed view.
 */
export function useSwipe(callbacks: SwipeCallbacks): SwipeHandlers {
  const touchStartRef = useRef<{ x: number; y: number } | undefined>(undefined);

  function onTouchStart(event: React.TouchEvent) {
    if (event.touches.length > 1 || isPinchZoomed()) {
      touchStartRef.current = undefined;
      return;
    }
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchEnd(event: React.TouchEvent) {
    const touchStart = touchStartRef.current;
    touchStartRef.current = undefined;
    if (!touchStart) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;

    if (Math.abs(deltaX) >= Math.abs(deltaY)) {
      if (Math.abs(deltaX) < swipeThresholdInPx) return;
      if (deltaX < 0) callbacks.onSwipeLeft();
      else callbacks.onSwipeRight();
    } else {
      if (Math.abs(deltaY) < swipeThresholdInPx) return;
      if (deltaY < 0) callbacks.onSwipeUp?.();
      else callbacks.onSwipeDown?.();
    }
  }

  return { onTouchStart, onTouchEnd };
}
